import { ModelJSON } from '../core/model';
import { Unit } from '../core/units';
import { VanState } from '../vans/vans';
import { sanitizeCustomFurniture } from '../furniture/library';

/** Projektionsformat von CampSketch (eine JSON-Datei). */
export interface ProjectData {
  version: 1;
  model: ModelJSON;
  van: VanState;
  units: Unit;
  /** Eigene Möbel der Bibliothek — optional, ältere Dateien haben das Feld nicht. */
  furniture?: ReturnType<typeof sanitizeCustomFurniture>[];
}

const AUTOSAVE_KEY = 'campsketch.autosave.v1';

/** Projekt als Datei herunterladen. */
export function saveProjectFile(project: ProjectData): void {
  const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'camper-ausbau.campsketch.json';
  a.click();
  // Erst nach dem Start des Downloads freigeben (sofortiges Revoke bricht ihn in manchen Browsern ab)
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Projekt aus Datei laden. */
export function openProjectFile(): Promise<ProjectData> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.campsketch.json,application/json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          resolve(validateProject(JSON.parse(String(reader.result))));
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    };
    input.click();
  });
}

/** Automatisch im Browser speichern. */
export function autosave(project: ProjectData): void {
  try {
    localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(project));
  } catch {
    // Speicher voll / privater Modus — still ignorieren
  }
}

export function loadAutosave(): ProjectData | null {
  try {
    const raw = localStorage.getItem(AUTOSAVE_KEY);
    if (!raw) return null;
    return validateProject(JSON.parse(raw));
  } catch {
    // Beschädigter Autosave darf die App nie blockieren → verwerfen
    clearAutosave();
    return null;
  }
}

export function clearAutosave(): void {
  try {
    localStorage.removeItem(AUTOSAVE_KEY);
  } catch {
    // ignorieren
  }
}

const UNITS: Unit[] = ['mm', 'cm', 'm'];

/**
 * Prüft eine geladene Projektdatei vollständig, bevor irgendetwas übernommen wird.
 * Wirft bei ungültigen Daten; fehlende Einheit fällt auf cm zurück.
 */
export function validateProject(data: unknown): ProjectData {
  const fail = (why: string): never => {
    throw new Error(`Ungültige Projektdatei: ${why}`);
  };
  if (!data || typeof data !== 'object') fail('kein Objekt');
  const d = data as Record<string, unknown>;
  if (d.version !== 1) fail(`unbekannte Version ${String(d.version)}`);
  const m = d.model as Record<string, unknown> | undefined;
  if (!m || typeof m !== 'object') fail('Modell fehlt');
  for (const key of ['vertices', 'edges', 'faces', 'guides'] as const) {
    if (!Array.isArray(m![key])) fail(`Modell.${key} fehlt`);
  }
  if (typeof m!.nextId !== 'number') fail('Modell.nextId fehlt');
  const v = d.van as Record<string, unknown> | undefined;
  if (!v || typeof v !== 'object') fail('Van fehlt');
  for (const key of ['length', 'width', 'height'] as const) {
    if (typeof v![key] !== 'number' || !Number.isFinite(v![key] as number) || (v![key] as number) <= 0) fail(`Van.${key} ungültig`);
  }
  if (typeof v!.templateId !== 'string') fail('Van.templateId fehlt');
  const units = UNITS.includes(d.units as Unit) ? (d.units as Unit) : 'cm';
  // Eigene Möbel: einzeln prüfen, ungültige Einträge verwerfen (statt die Datei zu verwerfen)
  const furniture = Array.isArray(d.furniture)
    ? d.furniture.map((entry) => sanitizeCustomFurniture(entry)).filter((t) => t !== null)
    : [];
  return {
    version: 1,
    model: m as unknown as ModelJSON,
    van: { ...(v as unknown as VanState), visible: v!.visible !== false, arches: v!.arches !== false },
    units,
    furniture
  };
}
